import * as THREE from 'three'
import RAPIER from '@dimforge/rapier3d-compat'
import { PointerLockControls } from 'three/addons/controls/PointerLockControls.js'
import { PlayerVitality } from './player/vitality'
import { WeaponInventory } from './weapons/inventory'
import { KeycardInventory } from './world/keycards'
import { colliderRegistry } from './world/collider-registry'

export interface FrameCollisionInfo {
  id?: string
  name?: string
  sourceTag?: string
  category?: string
  normal: [number, number, number]
  toi: number
}

export interface PlayerCollisionTelemetry {
  numCollisions: number
  collisions: FrameCollisionInfo[]
  speed: number
  isStuck: boolean
  desiredTranslation: [number, number, number]
}

const CAPSULE_RADIUS = 0.32
const CAPSULE_HALF_HEIGHT = 0.58
const EYE_OFFSET = 0.65
const WALK_SPEED = 6
const SPRINT_SPEED = 10
const JUMP_SPEED = 5
const GRAVITY = -15

const THIRD_PERSON_DISTANCE = 3.2
const THIRD_PERSON_SHOULDER_OFFSET = 0.45
const THIRD_PERSON_HEIGHT_OFFSET = 0.25
const CAMERA_COLLISION_PADDING = 0.25

export type CameraViewMode = 'first-person' | 'third-person'

export class PlayerController {
  readonly controls: PointerLockControls
  readonly vitality = new PlayerVitality()
  readonly weapons = new WeaponInventory()
  readonly keycards = new KeycardInventory()

  readonly body: RAPIER.RigidBody
  readonly collider: RAPIER.Collider

  private lastTelemetry: PlayerCollisionTelemetry = {
    numCollisions: 0,
    collisions: [],
    speed: 0,
    isStuck: false,
    desiredTranslation: [0, 0, 0],
  }

  getPosition(): THREE.Vector3 {
    return this.currPosition.clone()
  }

  get rigidBody(): RAPIER.RigidBody {
    return this.body
  }

  getCollisionTelemetry(): PlayerCollisionTelemetry {
    return this.lastTelemetry
  }
  private readonly characterController: RAPIER.KinematicCharacterController
  private readonly keys = new Set<string>()
  private jumpRequested = false
  private verticalVelocity = 0

  private readonly camera: THREE.PerspectiveCamera
  private readonly world: RAPIER.World
  private readonly meshGroup: THREE.Group

  private readonly forward = new THREE.Vector3()
  private readonly right = new THREE.Vector3()
  private readonly move = new THREE.Vector3()

  private readonly prevPosition = new THREE.Vector3()
  private readonly currPosition = new THREE.Vector3()
  private readonly interpolatedPosition = new THREE.Vector3()
  private readonly eyePosition = new THREE.Vector3()
  private readonly targetCamPos = new THREE.Vector3()
  private readonly rayDir = new THREE.Vector3()
  private readonly camRay = new RAPIER.Ray({ x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: 1 })
  private readonly desiredTranslation = { x: 0, y: 0, z: 0 }
  private readonly nextTranslation = { x: 0, y: 0, z: 0 }

  private viewMode: CameraViewMode = 'first-person'
  private targetCameraDistance = THIRD_PERSON_DISTANCE
  private cameraDistance = THIRD_PERSON_DISTANCE

  constructor(
    camera: THREE.PerspectiveCamera,
    world: RAPIER.World,
    scene: THREE.Scene,
    spawn: THREE.Vector3,
    initialFacingRad = 0,
  ) {
    this.camera = camera
    this.world = world
    this.controls = new PointerLockControls(camera, typeof document !== 'undefined' ? document.body : undefined)
    if (initialFacingRad) {
      this.setLookDirection(initialFacingRad, 0)
    }

    this.body = world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(spawn.x, spawn.y, spawn.z),
    )
    this.collider = world.createCollider(
      RAPIER.ColliderDesc.capsule(CAPSULE_HALF_HEIGHT, CAPSULE_RADIUS),
      this.body,
    )

    this.characterController = world.createCharacterController(0.01)
    this.characterController.enableAutostep(0.4, 0.2, true)
    this.characterController.setMaxSlopeClimbAngle((45 * Math.PI) / 180)
    this.characterController.enableSnapToGround(0.3)

    // Player third-person visual mesh rig
    this.meshGroup = new THREE.Group()
    const suitMat = new THREE.MeshStandardMaterial({
      color: 0x00ccff,
      roughness: 0.3,
      metalness: 0.8,
    })
    const bodyMesh = new THREE.Mesh(
      new THREE.CapsuleGeometry(CAPSULE_RADIUS, CAPSULE_HALF_HEIGHT * 2, 8, 16),
      suitMat,
    )
    this.meshGroup.add(bodyMesh)

    const visorMat = new THREE.MeshStandardMaterial({
      color: 0xffaa00,
      emissive: 0xffaa00,
      emissiveIntensity: 0.8,
    })
    const visorMesh = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.15, 0.2), visorMat)
    visorMesh.position.set(0, 0.45, -0.35)
    this.meshGroup.add(visorMesh)

    this.meshGroup.position.copy(spawn)
    this.meshGroup.visible = false
    scene.add(this.meshGroup)

    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', (event) => {
        this.keys.add(event.code)
        if (event.code === 'Space') {
          event.preventDefault()
          this.jumpRequested = true
        }
        if (event.code === 'KeyV') {
          this.toggleViewMode()
        }
        if (event.code.startsWith('Digit')) {
          const digit = Number(event.code.replace('Digit', ''))
          const slot = digit === 0 ? 10 : digit
          this.weapons.selectSlot(slot)
        }
        if (event.code === 'KeyQ') {
          this.weapons.prevWeapon()
        }
        if (event.code === 'KeyE') {
          this.weapons.nextWeapon()
        }
      })
      document.addEventListener('keyup', (event) => this.keys.delete(event.code))

      document.addEventListener('wheel', (event) => {
        if (!this.controls.isLocked) return
        if (event.deltaY > 0) {
          if (this.viewMode === 'first-person') {
            this.setViewMode('third-person')
            this.targetCameraDistance = 2.0
          } else {
            this.targetCameraDistance = Math.min(6.0, this.targetCameraDistance + 0.5)
          }
        } else if (event.deltaY < 0) {
          if (this.viewMode === 'third-person') {
            this.targetCameraDistance = Math.max(1.5, this.targetCameraDistance - 0.5)
            if (this.targetCameraDistance <= 1.5) {
              this.setViewMode('first-person')
            }
          }
        }
      })
    }
  }

  toggleViewMode(): void {
    this.setViewMode(this.viewMode === 'first-person' ? 'third-person' : 'first-person')
  }

  setViewMode(mode: CameraViewMode): void {
    this.viewMode = mode
    this.meshGroup.visible = mode === 'third-person'
  }

  getViewMode(): CameraViewMode {
    return this.viewMode
  }

  setKeys(keys: string[]): void {
    this.keys.clear()
    for (const k of keys) this.keys.add(k)
  }

  applyMouseDelta(movementX: number, movementY: number): void {
    const euler = new THREE.Euler(0, 0, 0, 'YXZ')
    euler.setFromQuaternion(this.camera.quaternion)
    euler.y -= movementX * 0.002
    euler.x -= movementY * 0.002
    const PI_2 = Math.PI / 2
    euler.x = Math.max(-PI_2, Math.min(PI_2, euler.x))
    this.camera.quaternion.setFromEuler(euler)
    this.camera.updateMatrixWorld(true)
  }

  setLookDirection(yaw: number, pitch = 0): void {
    const euler = new THREE.Euler(pitch, yaw, 0, 'YXZ')
    this.camera.quaternion.setFromEuler(euler)
    this.camera.updateMatrixWorld(true)
  }

  get position(): THREE.Vector3 {
    return this.currPosition
  }

  update(dt: number = 1 / 60) {
    this.weapons.update(dt)
    this.prevPosition.copy(this.currPosition)

    this.camera.getWorldDirection(this.forward)
    this.forward.y = 0
    this.forward.normalize()
    this.right.crossVectors(this.forward, this.camera.up)

    this.move.set(0, 0, 0)
    if (this.keys.has('KeyW')) this.move.add(this.forward)
    if (this.keys.has('KeyS')) this.move.sub(this.forward)
    if (this.keys.has('KeyD')) this.move.add(this.right)
    if (this.keys.has('KeyA')) this.move.sub(this.right)

    const isSprinting = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight')
    const speed = isSprinting ? SPRINT_SPEED : WALK_SPEED

    const hasHorizontalMove = this.move.lengthSq() > 0
    if (hasHorizontalMove) {
      this.move.normalize().multiplyScalar(speed)
    }

    const wasGrounded = this.characterController.computedGrounded()

    if (wasGrounded) {
      if (this.jumpRequested) {
        this.verticalVelocity = JUMP_SPEED
        this.characterController.disableSnapToGround()
      } else {
        this.verticalVelocity = -0.5
        this.characterController.enableSnapToGround(0.3)
      }
    } else {
      this.verticalVelocity += GRAVITY * dt
    }
    this.jumpRequested = false

    this.desiredTranslation.x = this.move.x * dt
    this.desiredTranslation.y = this.verticalVelocity * dt
    this.desiredTranslation.z = this.move.z * dt

    this.characterController.computeColliderMovement(this.collider, this.desiredTranslation)
    let correctedMovement = this.characterController.computedMovement()

    // Multi-pass vertical wall tangent slide projection (Source/Quake style)
    if (hasHorizontalMove) {
      const remainingDesired = {
        x: this.desiredTranslation.x,
        y: this.desiredTranslation.y,
        z: this.desiredTranslation.z,
      }

      for (let pass = 0; pass < 2; pass++) {
        const horizontalDesired = Math.hypot(remainingDesired.x, remainingDesired.z)
        const horizontalCorrected = Math.hypot(correctedMovement.x, correctedMovement.z)
        const numCollisions = this.characterController.numComputedCollisions()

        if (horizontalCorrected < horizontalDesired * 0.6 && numCollisions > 0) {
          let slid = false
          for (let i = 0; i < numCollisions; i++) {
            const col = this.characterController.computedCollision(i)
            if (!col || !col.normal1) continue

            const nx = col.normal1.x
            const ny = col.normal1.y
            const nz = col.normal1.z

            // Only slide on vertical wall surfaces (not floors or ceilings)
            if (Math.abs(ny) < 0.5) {
              const normalLen = Math.hypot(nx, nz)
              if (normalLen > 0.001) {
                const unitNx = nx / normalLen
                const unitNz = nz / normalLen
                const dot = remainingDesired.x * unitNx + remainingDesired.z * unitNz

                if (dot < 0) {
                  remainingDesired.x -= dot * unitNx
                  remainingDesired.z -= dot * unitNz

                  const slideVector = {
                    x: remainingDesired.x + unitNx * 0.002,
                    y: remainingDesired.y,
                    z: remainingDesired.z + unitNz * 0.002,
                  }

                  this.characterController.computeColliderMovement(this.collider, slideVector)
                  correctedMovement = this.characterController.computedMovement()
                  slid = true
                  break
                }
              }
            }
          }
          if (!slid) break
        } else {
          break
        }
      }
    }

    // Collect collision telemetry
    const numCol = this.characterController.numComputedCollisions()
    const collisionList: FrameCollisionInfo[] = []
    for (let i = 0; i < numCol; i++) {
      const col = this.characterController.computedCollision(i)
      if (!col) continue
      const handle = (col as any).collider?.handle ?? (col as any).handle2
      const meta = handle !== undefined ? colliderRegistry.get(handle) : undefined
      collisionList.push({
        id: meta?.id,
        name: meta?.name,
        sourceTag: meta?.sourceTag,
        category: meta?.category,
        normal: [
          col.normal1 ? Number(col.normal1.x.toFixed(3)) : 0,
          col.normal1 ? Number(col.normal1.y.toFixed(3)) : 0,
          col.normal1 ? Number(col.normal1.z.toFixed(3)) : 0,
        ],
        toi: col.toi ? Number(col.toi.toFixed(4)) : 0,
      })
    }

    const horizontalSpeed = Math.hypot(correctedMovement.x, correctedMovement.z) / Math.max(0.0001, dt)
    const isStuck = hasHorizontalMove && horizontalSpeed < 0.05

    this.lastTelemetry = {
      numCollisions: numCol,
      collisions: collisionList,
      speed: Number(horizontalSpeed.toFixed(2)),
      isStuck,
      desiredTranslation: [
        Number(this.desiredTranslation.x.toFixed(4)),
        Number(this.desiredTranslation.y.toFixed(4)),
        Number(this.desiredTranslation.z.toFixed(4)),
      ],
    }

    const currentPos = this.body.translation()
    this.nextTranslation.x = currentPos.x + correctedMovement.x
    this.nextTranslation.y = currentPos.y + correctedMovement.y
    this.nextTranslation.z = currentPos.z + correctedMovement.z
    this.body.setNextKinematicTranslation(this.nextTranslation)
  }

  postPhysicsStep() {
    const pos = this.body.translation()
    this.currPosition.set(pos.x, pos.y, pos.z)
  }

  render(alpha: number) {
    this.interpolatedPosition.lerpVectors(this.prevPosition, this.currPosition, alpha)

    this.eyePosition.set(
      this.interpolatedPosition.x,
      this.interpolatedPosition.y + EYE_OFFSET,
      this.interpolatedPosition.z,
    )

    this.camera.getWorldDirection(this.forward)

    if (this.viewMode === 'first-person') {
      this.camera.position.copy(this.eyePosition)
      this.meshGroup.visible = false
    } else {
      this.meshGroup.visible = true
      this.meshGroup.position.copy(this.interpolatedPosition)
      const yaw = Math.atan2(-this.forward.x, -this.forward.z)
      this.meshGroup.rotation.y = yaw

      this.right.crossVectors(this.forward, this.camera.up).normalize()

      this.targetCamPos
        .copy(this.eyePosition)
        .addScaledVector(this.camera.up, THIRD_PERSON_HEIGHT_OFFSET)
        .addScaledVector(this.right, THIRD_PERSON_SHOULDER_OFFSET)
        .addScaledVector(this.forward, -this.targetCameraDistance)

      this.rayDir.subVectors(this.targetCamPos, this.eyePosition)
      const maxRayDist = this.rayDir.length()
      if (maxRayDist > 0.001) {
        this.rayDir.normalize()

        this.camRay.origin.x = this.eyePosition.x
        this.camRay.origin.y = this.eyePosition.y
        this.camRay.origin.z = this.eyePosition.z
        this.camRay.dir.x = this.rayDir.x
        this.camRay.dir.y = this.rayDir.y
        this.camRay.dir.z = this.rayDir.z

        const hit = this.world.castRay(
          this.camRay,
          maxRayDist,
          true,
          undefined,
          undefined,
          this.collider,
        )

        let targetDist = maxRayDist
        if (hit && hit.timeOfImpact < maxRayDist) {
          targetDist = Math.max(0.3, hit.timeOfImpact - CAMERA_COLLISION_PADDING)
        }

        // Smooth camera distance transitions
        this.cameraDistance += (targetDist - this.cameraDistance) * 0.25
        this.camera.position.copy(this.eyePosition).addScaledVector(this.rayDir, this.cameraDistance)
      } else {
        this.camera.position.copy(this.eyePosition)
      }
    }
  }
}
